package com.schoolconnect.app.repository.attendance

import com.schoolconnect.app.datasource.network.KtorApiClient
import com.schoolconnect.app.datasource.network.request.QuickMarkRequest
import com.schoolconnect.app.datasource.network.response.TeacherAttendanceInsightsResponse
import com.schoolconnect.app.datasource.network.response.AttendanceInsightsResponse
import com.schoolconnect.app.datasource.network.response.TeacherAttendanceInsightStudent
import com.schoolconnect.app.datasource.network.response.TeacherAttendanceStatisticsResponse
import com.schoolconnect.app.feature.common.PaginationPolicy
import com.schoolconnect.app.util.scLog
import com.schoolconnect.app.repository.common.RepositoryCache
import com.schoolconnect.app.repository.common.tenantCacheKey
import kotlinx.coroutines.CoroutineScope

object AttendanceInsightsRepository {
    private const val TAG = "[DATA_RETRIEVE_SC] AttendanceInsightsRepository"
    private const val QUICK_MARK_PAGE_SIZE = 100
    private const val QUICK_MARK_MAX_PAGES = 10
    private val pageCache = RepositoryCache<TeacherAttendanceInsightsResponse>()
    private val schoolPageCache = RepositoryCache<AttendanceInsightsResponse>()
    private val statisticsCache = RepositoryCache<TeacherAttendanceStatisticsResponse>()

    suspend fun loadSchoolPage(
        schoolId: String,
        classSectionId: String? = null,
        band: String? = null,
        pageIndex: Int = 0,
        pageSize: Int = PaginationPolicy.DEFAULT_PAGE_SIZE,
        sortBy: String? = null,
        order: String? = null,
        startDate: String? = null,
        endDate: String? = null,
        query: String? = null,
        status: String? = null,
        forceRemote: Boolean = false,
        refreshScope: CoroutineScope
    ): Result<AttendanceInsightsResponse> {
        val safePage = pageIndex.coerceAtLeast(0) + 1
        val safeLimit = pageSize.coerceIn(1, 100)
        val normalizedSchoolId = schoolId.trim()
        val normalizedClassSectionId = classSectionId?.trim()?.takeIf { it.isNotBlank() }
        val normalizedSortBy = sortBy?.trim()?.takeIf { it.isNotBlank() }
        val normalizedOrder = order?.trim()?.lowercase()?.takeIf { it == "asc" || it == "desc" }
        val normalizedQuery = query?.trim()?.takeIf { it.isNotBlank() }
        val normalizedStatus = status?.trim()?.takeIf { it.isNotBlank() }
        val key = tenantCacheKey("school-attendance:$normalizedSchoolId:$normalizedClassSectionId:$band:$startDate:$endDate:$normalizedQuery:$normalizedStatus:$safePage:$safeLimit:$normalizedSortBy:$normalizedOrder")
        if (forceRemote) schoolPageCache.clear()
        scLog("$TAG loadSchoolPage start school=$normalizedSchoolId classSectionId=${normalizedClassSectionId ?: "<any>"} startDate=${startDate ?: "<any>"} endDate=${endDate ?: "<any>"} page=$safePage limit=$safeLimit sortBy=${normalizedSortBy ?: "<default>"} order=${normalizedOrder ?: "<default>"} forceRemote=$forceRemote")
        return schoolPageCache.load(refreshScope, TAG, "loadSchoolPage", key) {
            KtorApiClient.getSchoolAttendanceInsights(
                classSectionId = normalizedClassSectionId,
                band = band,
                page = safePage,
                limit = safeLimit,
                sortBy = normalizedSortBy,
                order = normalizedOrder,
                startDate = startDate,
                endDate = endDate,
                query = normalizedQuery,
                status = normalizedStatus
            ).onFailure { throwable -> scLog("$TAG loadSchoolPage failure school=$normalizedSchoolId page=$safePage", throwable) }
        }
    }

    suspend fun loadPage(
        startDate: String? = null,
        endDate: String? = null,
        band: String? = null,
        classSectionId: String? = null,
        pageIndex: Int = 0,
        pageSize: Int = PaginationPolicy.DEFAULT_PAGE_SIZE,
        sortBy: String? = null,
        order: String? = null,
        query: String? = null,
        status: String? = null,
        forceRemote: Boolean = false,
        refreshScope: CoroutineScope
    ): Result<TeacherAttendanceInsightsResponse> {
        val safePage = pageIndex.coerceAtLeast(0) + 1
        val safeLimit = pageSize.coerceIn(1, 100)
        val normalizedClassSectionId = classSectionId?.trim()?.takeIf { it.isNotBlank() }
        val normalizedSortBy = sortBy?.trim()?.takeIf { it.isNotBlank() }
        val normalizedOrder = order?.trim()?.lowercase()?.takeIf { it == "asc" || it == "desc" }
        val normalizedQuery = query?.trim()?.takeIf { it.isNotBlank() }
        val normalizedStatus = status?.trim()?.takeIf { it.isNotBlank() }
        scLog("$TAG loadPage start startDate=${startDate ?: "<any>"} endDate=${endDate ?: "<any>"} band=${band ?: "<any>"} classSectionId=${normalizedClassSectionId ?: "<any>"} page=$safePage limit=$safeLimit sortBy=${normalizedSortBy ?: "<default>"} order=${normalizedOrder ?: "<default>"} forceRemote=$forceRemote")
        val key = tenantCacheKey("teacher-attendance:$startDate:$endDate:$band:$normalizedClassSectionId:$normalizedQuery:$normalizedStatus:$safePage:$safeLimit:$normalizedSortBy:$normalizedOrder")
        if (forceRemote) pageCache.clear()
        return pageCache.load(refreshScope, TAG, "loadPage", key) { KtorApiClient.getTeacherAttendanceInsights(
            startDate = startDate,
            endDate = endDate,
            band = band,
            classSectionId = normalizedClassSectionId,
            page = safePage,
            limit = safeLimit,
            sortBy = normalizedSortBy,
            order = normalizedOrder,
            query = normalizedQuery,
            status = normalizedStatus
        ).onFailure { throwable -> scLog("$TAG loadPage failure page=$safePage", throwable) } }
    }

    suspend fun loadStatistics(
        startDate: String? = null,
        endDate: String? = null,
        forceRemote: Boolean = false,
        refreshScope: CoroutineScope
    ): Result<TeacherAttendanceStatisticsResponse> {
        scLog("$TAG loadStatistics start startDate=${startDate ?: "<any>"} endDate=${endDate ?: "<any>"} forceRemote=$forceRemote")
        val key = tenantCacheKey("teacher-attendance-statistics:$startDate:$endDate")
        if (forceRemote) statisticsCache.clear()
        return statisticsCache.load(refreshScope, TAG, "loadStatistics", key) { KtorApiClient.getTeacherAttendanceStatistics(startDate, endDate)
            .onFailure { throwable -> scLog("$TAG loadStatistics failure", throwable) } }
    }

    suspend fun loadAll(
        startDate: String? = null,
        endDate: String? = null,
        band: String? = null,
        classSectionId: String? = null,
        sortBy: String? = null,
        order: String? = null,
        query: String? = null,
        status: String? = null,
        forceRemote: Boolean = false,
        refreshScope: CoroutineScope
    ): Result<TeacherAttendanceInsightsResponse> {
        val firstPage = loadPage(
            startDate = startDate,
            endDate = endDate,
            band = band,
            classSectionId = classSectionId,
            pageIndex = 0,
            pageSize = QUICK_MARK_PAGE_SIZE,
            sortBy = sortBy,
            order = order,
            query = query,
            status = status,
            forceRemote = forceRemote,
            refreshScope = refreshScope
        ).getOrElse { return Result.failure(it) }
        val total = firstPage.total ?: firstPage.totalStudents ?: firstPage.students.size
        val allStudents = firstPage.students.toMutableList()
        val pageCount = (total + QUICK_MARK_PAGE_SIZE - 1) / QUICK_MARK_PAGE_SIZE
        if (pageCount > QUICK_MARK_MAX_PAGES) {
            return Result.failure(IllegalStateException("Attendance insights exceeded the supported page limit."))
        }

        for (pageIndex in 1 until pageCount) {
            val page = loadPage(
                startDate = startDate,
                endDate = endDate,
                band = band,
                classSectionId = classSectionId,
                pageIndex = pageIndex,
                pageSize = QUICK_MARK_PAGE_SIZE,
                sortBy = sortBy,
                order = order,
                query = query,
                status = status,
                forceRemote = forceRemote,
                refreshScope = refreshScope
            ).getOrElse { return Result.failure(it) }
            allStudents += page.students
        }

        return Result.success(firstPage.copy(students = allStudents.take(total), page = 1, limit = QUICK_MARK_PAGE_SIZE))
    }

    suspend fun quickMarkAttendance(request: QuickMarkRequest): Result<Unit> {
        scLog("$TAG quickMarkAttendance start classId=${request.classId} attendanceDate=${request.attendanceDate} exceptions=${request.exceptions.size}")
        return KtorApiClient.quickMarkAttendance(request)
            .onSuccess { scLog("$TAG quickMarkAttendance success classId=${request.classId} attendanceDate=${request.attendanceDate}") }
            .onFailure { throwable -> scLog("$TAG quickMarkAttendance failure classId=${request.classId}", throwable) }
    }
}
